import React, { createContext, useState, useMemo, useContext } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useSelector } from 'react-redux';
import { getApiBaseUrl } from '../platform/api';

// Dark theme variants
const DARK_THEMES = {
  midnight: {
    name: 'Midnight Blue',
    colors: {
      background: {
        default: '#0d1117',
        paper: '#21262d',
      },
      text: {
        primary: 'rgba(240, 246, 252, 0.9)',
        secondary: 'rgba(240, 246, 252, 0.7)',
      },
      typography: {
        body2: '#c9d1d9',
      },
    },
  },
  original: {
    name: 'Dark',
    colors: {
      background: {
        default: '#121212',
        paper: '#1e1e1e',
      },
      text: {
        primary: 'rgba(255, 255, 255, 0.87)',
        secondary: 'rgba(255, 255, 255, 0.6)',
      },
      typography: {
        body2: '#cccccc',
      },
    },
  },
  softer: {
    name: 'Softer Dark',
    colors: {
      background: {
        default: '#1a1a1a',
        paper: '#2d2d2d',
      },
      text: {
        primary: 'rgba(255, 255, 255, 0.9)',
        secondary: 'rgba(255, 255, 255, 0.7)',
      },
      typography: {
        body2: '#d4d4d4',
      },
    },
  },
  
};
const DARKWING_THEME = {
    name: 'Darkwing Duck',
    colors: {
      background: {
        default: '#1a0f2e', // Deep purple night sky
        paper: '#2d1b4e', // Slightly lighter purple
      },
      text: {
        primary: '#e8d4f2', // Light lavender
        secondary: '#b8a0cc', // Muted purple
      },
      typography: {
        body2: '#d4c4e0',
      },
    },
    accent: {
      primary: '#9d4edd', // Darkwing Duck purple
      secondary: '#ff006e', // Danger Duck pink/magenta
    },
};

// Create the context
export const ThemeContext = createContext({
  toggleColorMode: () => {},
  setDarkTheme: () => {},
  mode: 'light',
  darkTheme: 'midnight',
  availableDarkThemes: DARK_THEMES,
});

// Create a theme context provider component
export const ThemeProvider = ({ children }) => {
  const userKey = useSelector(state => state.auth.isAuthenticated ? state.auth.user?.user : null);
  // Native apps use an explicit API origin; deployed web/PWA uses same-origin APIs.
  const isStaging = (getApiBaseUrl() || window.location.origin) === 'https://staging.connectapp365.com';
  const canUseDarkwing = isStaging && ['2', '10'].includes(String(userKey));
  const availableDarkThemes = useMemo(() => canUseDarkwing
    ? { ...DARK_THEMES, darkwing: DARKWING_THEME }
    : DARK_THEMES, [canUseDarkwing]);
  const [mode, setMode] = useState(() => localStorage.getItem('themeMode') || 'light');
  const [preferredDarkTheme, setDarkThemeState] = useState(() => localStorage.getItem('darkTheme') || 'midnight');
  // A stored preference must never enable the theme for another account/environment.
  const darkTheme = availableDarkThemes[preferredDarkTheme] ? preferredDarkTheme : 'midnight';

  // Theme toggle function
  const toggleColorMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode); // Save to localStorage
      return newMode;
    });
  };

  // Dark theme variant setter
  const setDarkTheme = (themeKey) => {
    if (availableDarkThemes[themeKey]) {
      setDarkThemeState(themeKey);
      localStorage.setItem('darkTheme', themeKey);
    }
  };

  // Get current dark theme configuration
  const currentDarkTheme = availableDarkThemes[darkTheme];

  // Create the theme based on current mode and dark theme variant
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          background: {
            default: mode === 'light' ? '#f5f5f5' : currentDarkTheme.colors.background.default,
            paper: mode === 'light' ? '#ffffff' : currentDarkTheme.colors.background.paper,
          },
          primary: {
            main: mode === 'dark' && currentDarkTheme.accent?.primary 
              ? currentDarkTheme.accent.primary 
              : '#007bff',
          },
          secondary: {
            main: mode === 'dark' && currentDarkTheme.accent?.secondary 
              ? currentDarkTheme.accent.secondary 
              : '#f50057',
          },
          text: {
            primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : currentDarkTheme.colors.text.primary,
            secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : currentDarkTheme.colors.text.secondary,
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h5: {
            fontWeight: 600,
            letterSpacing: '0.5px',
          },
          h6: {
            fontWeight: 600,
          },
          body1: {
            fontSize: '1.1rem',
          },
          body2: {
            color: mode === 'light' ? '#666666' : currentDarkTheme.colors.typography.body2,
          },
          button: {
            fontWeight: 'bold'
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: ({ theme }) => ({
                fontSize: '0.95rem',
                lineHeight: 1.4,
                padding: '10px 12px',
                // backgroundColor: theme.palette.mode === 'dark' ? '#111827' : '#1f2937',
                color: '#ffffff',
                opacity: 1,
              }),
              arrow: {
                fontSize: '0.95rem',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
        },
      }),
    [mode, currentDarkTheme]
  );

  const contextValue = useMemo(
    () => ({
      toggleColorMode,
      setDarkTheme,
      mode,
      darkTheme,
      availableDarkThemes,
    }),
    [mode, darkTheme, availableDarkThemes]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline /> {/* Provides consistent baseline styles */}
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = () => useContext(ThemeContext);
